import React, { useEffect, useState, useCallback } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "../App";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import { ArrowLeft, Clock, Users, MessageCircle, Send, Trash2 } from "lucide-react";
import { format } from "date-fns";
import SocialShare from "../components/SocialShare";

const PostDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [post, setPost] = useState(null);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState("");
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [postRes, commentsRes] = await Promise.all([
        axios.get(`${API}/posts/${id}`),
        axios.get(`${API}/posts/${id}/comments`)
      ]);
      setPost(postRes.data);
      setComments(commentsRes.data);
    } catch (error) {
      console.error("Error fetching post:", error);
      toast.error("Failed to load post");
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const formatDate = (dateStr) => {
    try {
      return format(new Date(dateStr), "MMMM d, yyyy 'at' h:mm a");
    } catch {
      return dateStr;
    }
  };

  const handleSubmitComment = async (e) => {
    e.preventDefault();
    
    if (!user) {
      toast.error("Please login to comment");
      navigate("/login");
      return;
    }

    if (!newComment.trim()) {
      toast.error("Please enter a comment");
      return;
    }

    setSubmitting(true);
    try {
      const response = await axios.post(
        `${API}/posts/${id}/comments`,
        { content: newComment },
        { withCredentials: true }
      );
      setComments([response.data, ...comments]);
      setNewComment("");
      toast.success("Comment posted!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to post comment");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteComment = async (commentId) => {
    try {
      await axios.delete(`${API}/comments/${commentId}`, { withCredentials: true });
      setComments(comments.filter(c => c.comment_id !== commentId));
      toast.success("Comment deleted");
    } catch (error) {
      toast.error("Failed to delete comment");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-900"></div>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="font-heading text-2xl font-semibold text-gray-900 mb-4">Post Not Found</h2>
          <Button asChild>
            <Link to="/">Go Home</Link>
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero */}
      <section className="bg-white border-b">
        <div className="container-custom py-8">
          <Link to="/" className="inline-flex items-center text-gray-600 hover:text-primary-900 mb-6 transition-colors">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Home
          </Link>
        </div>
      </section>

      {/* Post Content */}
      <section className="section-padding">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto">
            <Card className="overflow-hidden mb-8" data-testid="post-detail">
              {post.image_url && (
                <div className="aspect-video overflow-hidden">
                  <img src={post.image_url} alt={post.title} className="w-full h-full object-cover" />
                </div>
              )}
              <CardContent className="p-8">
                <div className="flex items-center gap-2 mb-4">
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                    post.category === 'news' ? 'bg-blue-100 text-blue-700' :
                    post.category === 'announcement' ? 'bg-amber-100 text-amber-700' :
                    'bg-green-100 text-green-700'
                  }`}>
                    {post.category}
                  </span>
                  {post.committee && (
                    <Link 
                      to={`/committee/${post.committee}`}
                      className="text-xs text-gray-500 hover:text-primary-900 transition-colors"
                    >
                      {post.committee}
                    </Link>
                  )}
                </div>

                <h1 className="font-heading text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                  {post.title}
                </h1>

                <div className="flex items-center gap-6 text-sm text-gray-500 mb-8 pb-8 border-b">
                  <div className="flex items-center gap-2">
                    <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center">
                      <Users className="w-5 h-5 text-primary-900" />
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">{post.author_name}</p>
                      <p className="text-xs">Author</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <Clock className="w-4 h-4" />
                    {formatDate(post.created_at)}
                  </div>
                </div>

                <div className="prose prose-lg max-w-none mb-8">
                  <p className="text-gray-700 leading-relaxed whitespace-pre-wrap">{post.content}</p>
                </div>

                {/* Social Sharing */}
                <div className="pt-6 border-t">
                  <SocialShare 
                    url={window.location.href}
                    title={post.title}
                  />
                </div>
              </CardContent>
            </Card>

            {/* Comments Section */}
            <Card className="p-8" data-testid="comments-section">
              <h2 className="font-heading text-xl font-semibold text-gray-900 mb-6 flex items-center gap-2">
                <MessageCircle className="w-5 h-5" />
                Comments ({comments.length})
              </h2>

              {/* Comment Form */}
              <form onSubmit={handleSubmitComment} className="mb-8">
                {user ? (
                  <div className="flex gap-4">
                    <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
                      {user.picture ? (
                        <img src={user.picture} alt={user.name} className="w-10 h-10 rounded-full object-cover" />
                      ) : (
                        <Users className="w-5 h-5 text-primary-900" />
                      )}
                    </div>
                    <div className="flex-grow">
                      <Textarea
                        value={newComment}
                        onChange={(e) => setNewComment(e.target.value)}
                        placeholder="Write a comment..."
                        rows={3}
                        className="mb-3"
                        data-testid="comment-input"
                      />
                      <Button 
                        type="submit" 
                        className="btn-primary"
                        disabled={submitting}
                        data-testid="submit-comment"
                      >
                        {submitting ? (
                          <>
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                            Posting...
                          </>
                        ) : (
                          <>
                            <Send className="w-4 h-4 mr-2" />
                            Post Comment
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="bg-gray-50 rounded-lg p-6 text-center">
                    <p className="text-gray-600 mb-4">Login to join the conversation</p>
                    <Button asChild className="btn-primary">
                      <Link to="/login">Login to Comment</Link>
                    </Button>
                  </div>
                )}
              </form>

              {/* Comments List */}
              {comments.length === 0 ? (
                <div className="text-center py-8">
                  <MessageCircle className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500">No comments yet. Be the first to comment!</p>
                </div>
              ) : (
                <div className="space-y-6">
                  {comments.map((comment) => (
                    <div key={comment.comment_id} className="flex gap-4" data-testid={`comment-${comment.comment_id}`}>
                      <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0">
                        {comment.user_picture ? (
                          <img src={comment.user_picture} alt={comment.user_name} className="w-10 h-10 rounded-full object-cover" />
                        ) : (
                          <Users className="w-5 h-5 text-gray-500" />
                        )}
                      </div>
                      <div className="flex-grow">
                        <div className="bg-gray-50 rounded-lg p-4">
                          <div className="flex items-center justify-between mb-2">
                            <p className="font-medium text-gray-900">{comment.user_name}</p>
                            {(user?.user_id === comment.user_id || user?.is_admin) && (
                              <button 
                                onClick={() => handleDeleteComment(comment.comment_id)}
                                className="text-gray-400 hover:text-red-500 transition-colors"
                                data-testid={`delete-comment-${comment.comment_id}`}
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            )}
                          </div>
                          <p className="text-gray-700">{comment.content}</p>
                        </div>
                        <p className="text-xs text-gray-500 mt-2 ml-4">{formatDate(comment.created_at)}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Card>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PostDetail;
